import type { AisleDefinition } from '../types';
import { dedupeAislesByNumber } from './aisleMediaLinks';

export type RetailerLocationOption = { id: number; name: string };

export function formatInStoreLocationLabel(aisle: Pick<AisleDefinition, 'number' | 'name'>): string {
  const name = aisle.name?.trim();
  if (!name) return `Aisle ${aisle.number}`;
  const aislePrefix = new RegExp(`^aisle\\s*${aisle.number}\\b`, 'i');
  if (aislePrefix.test(name) || name.toLowerCase() === `aisle ${aisle.number}`) {
    return name;
  }
  return `${name}`;
}

export function resolveTaggedInStoreLocations(
  aisleNumbers: number[],
  aisles: AisleDefinition[],
): Array<{ number: number; label: string; aisle?: AisleDefinition }> {
  const byNumber = new Map(dedupeAislesByNumber(aisles).map((aisle) => [aisle.number, aisle]));
  return [...new Set(aisleNumbers)]
    .sort((a, b) => a - b)
    .map((number) => {
      const aisle = byNumber.get(number);
      return {
        number,
        label: aisle ? formatInStoreLocationLabel(aisle) : `Aisle ${number}`,
        aisle,
      };
    });
}

export function resolveTaggedRetailerLocations(
  locationIds: number[] | undefined,
  locations: RetailerLocationOption[],
): Array<{ id: number; label: string }> {
  const byId = new Map(locations.map((location) => [location.id, location]));
  return [...new Set(locationIds ?? [])]
    .filter((id) => id > 0)
    .sort((a, b) => {
      const nameA = byId.get(a)?.name ?? '';
      const nameB = byId.get(b)?.name ?? '';
      return nameA.localeCompare(nameB) || a - b;
    })
    .map((id) => ({
      id,
      label: byId.get(id)?.name ?? `Location #${id}`,
    }));
}

/** Resolve store-selected location ids to catalog entries (drops unknown ids). */
export function resolveStoreInstoreLocations(
  selectedIds: number[] | undefined,
  retailerLocations: RetailerLocationOption[],
): RetailerLocationOption[] {
  const byId = new Map(retailerLocations.map((location) => [location.id, location]));
  return [...new Set(selectedIds ?? [])]
    .filter((id) => id > 0)
    .map((id) => byId.get(id))
    .filter((location): location is RetailerLocationOption => location != null)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Prefer retailer catalog IDs; fall back to legacy aisle-number badges. */
export function resolveWalkthroughLocationBadges(
  locationIds: number[] | undefined,
  aisleNumbers: number[],
  locations: RetailerLocationOption[],
  aisles: AisleDefinition[],
): Array<{ key: string; label: string; aisleNumber?: number }> {
  const fromCatalog = resolveTaggedRetailerLocations(locationIds, locations);
  if (fromCatalog.length > 0) {
    return fromCatalog.map((item) => ({ key: `loc-${item.id}`, label: item.label }));
  }
  return resolveTaggedInStoreLocations(aisleNumbers, aisles).map((item) => ({
    key: `aisle-${item.number}`,
    label: item.label,
    aisleNumber: item.number,
  }));
}
